/**
 * beneficiaryService.js
 * Bénéficiaires SafeHeir — Supabase + AES-256-GCM sous Master Key.
 *
 * Résout NC-1 : remplace la clé v2 dérivable (HmacSHA256 uid:email)
 * par un chiffrement sous la MK non-extractable (DEC-016 / DEC-017).
 *
 * AAD format : 'safeheir-beneficiary-v1|' + item_id  (cohérent DEC-017)
 * Payload JSON chiffré : { name, email, phone, relation, message }
 *
 * Les 3 colonnes d'attribution (access_categories, designation_consent_at,
 * designation_form_version) et les 3 colonnes de consentement RGPD titulaire
 * (designator_consent_at, designator_consent_version, designator_consent_text_hash,
 * K81) sont stockées EN CLAIR côté serveur — dérogation assumée documentée
 * dans docs/02-security/attribution-data-model.md.
 * Elles ne font JAMAIS partie du payload chiffré.
 *
 * Toutes les fonctions de lecture/écriture exigent masterKey non-null.
 * Si masterKey === null → { success: false, error: 'Coffre verrouillé' }
 */

import { supabase } from './supabaseClient';
import { AAD_TRUSTED_CONTACT_V1, encryptData, decryptData } from './cryptoService';
import { TRANSMISSION_CONFIG } from '../config/transmissionConfig';
import { DESIGNATOR_CONSENT_VERSION, computeConsentTextHash } from '../config/rgpdConsentConfig';
import { isValidReleasePath } from '../config/releasePathConfig';

const AAD_PREFIX = 'safeheir-beneficiary-v1|';

export const aad = (itemId) => `${AAD_PREFIX}${itemId}`;

/**
 * Statuts terminaux : aucune action du titulaire (DEC-034, trigger PF-03 CP1.1).
 * Liste unique pour setReleasePath, setTrustedContact et resendInvitation.
 */
const TERMINAL_STATUSES = ['revoked', 'transmitted', 'expired'];

// Validation locale d'un email (même motif que send-email, F2a).
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * D1 — un bénéficiaire révoqué est conservé à titre de preuve mais exclu
 * de toute notion de transmission ou de couverture.
 * @param {{ status?: string } | null | undefined} b
 * @returns {boolean}
 */
export const isRevoked = (b) => b?.status === 'revoked';

const buildPayload = (data) => ({
  name:     (data.name?.trim() || `${data.firstName?.trim() || ''} ${data.lastName?.trim() || ''}`.trim()),
  email:    data.email?.trim().toLowerCase() || '',
  phone:    data.phone?.trim()   || null,
  relation: data.relation        || null,
  message:  data.message?.trim() || null,
});

// ─── Attribution ────────────────────────────────────────────────────────────

/** Version courante du formulaire de désignation — journalisée à chaque acte. */
export const CURRENT_DESIGNATION_FORM_VERSION = 'v1';

/**
 * Valide que toutes les catégories fournies existent dans TRANSMISSION_CONFIG.
 * T-S4-ATTR-03 — Spec V2 §4
 *
 * @param {unknown} categories
 * @returns {{ valid: boolean, invalidCategories?: string[] }}
 */
export function validateAccessCategories(categories) {
  if (!Array.isArray(categories)) {
    return { valid: false, invalidCategories: ['<not-an-array>'] };
  }
  if (categories.length === 0) return { valid: true };
  const known   = Object.keys(TRANSMISSION_CONFIG);
  const invalid = categories.filter((c) => !known.includes(c));
  return invalid.length === 0
    ? { valid: true }
    : { valid: false, invalidCategories: invalid };
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Génère un jeton de confirmation à usage unique.
 * Retourne { token, hash, expiresAt } :
 *   token    — 32 octets aléatoires encodés base64url (jamais stocké)
 *   hash     — SHA-256 hex de la chaîne token (stocké en DB)
 *   expiresAt — now + 30 jours ISO
 * Peut lever si crypto indisponible.
 */
const generateConfirmationToken = async () => {
  const rawBytes = crypto.getRandomValues(new Uint8Array(32));
  const token = btoa(String.fromCharCode(...rawBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  const hash = Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  return { token, hash, expiresAt };
};

/**
 * Calcule email_hash via l'Edge Function hash-email.
 * K46 — F2b : HMAC-SHA256(BENEFICIARY_HASH_SECRET, email_normalisé), hex.
 * La normalisation (trim + toLowerCase) est identique côté hash-email et send-email.
 * Retourne null en cas d'échec (réseau, secret absent) — l'appelant décide.
 */
const getEmailHash = async (email) => {
  try {
    const { data, error } = await supabase.functions.invoke('hash-email', {
      body: { email },
    });
    if (error || !data?.email_hash) return null;
    return data.email_hash;
  } catch {
    return null;
  }
};

// ─── Lecture ───────────────────────────────────────────────────────────────

/**
 * Retourne tous les bénéficiaires actifs déchiffrés sous MK.
 * D1 : les révoqués sont exclus par défaut (Dashboard, TransmissionRecap,
 * CheckinPage) ; { includeRevoked: true } les inclut (BeneficiariesPage).
 * select('*') inclut access_categories, designation_consent_at,
 * designation_form_version (ajoutés par T-S4-ATTR-01) et release_path,
 * key_version, revoked_at, revoked_reason, transmitted_at (PF-03 CP1) —
 * présents dans le spread { ...row, ...payload } sans mapping supplémentaire.
 * K46 : backfill lazy email_hash pour les lignes existantes (email_hash IS NULL).
 * T-S4-ATTR-03 — Spec V2 §4
 */
export const getAll = async (masterKey, { includeRevoked = false } = {}) => {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé', data: [], failedCount: 0 };
  try {
    const { data: rows, error } = await supabase
      .from('beneficiaries')
      .select('*')
      .eq('is_active', true)
      .order('priority', { ascending: true });

    if (error) return { success: false, error: error.message, data: [], failedCount: 0 };

    const decrypted = [];
    let failedCount = 0;

    for (const row of rows.filter((r) => includeRevoked || !isRevoked(r))) {
      try {
        const payload = await decryptData(row.encrypted_data, row.iv, masterKey, aad(row.id));

        // PF-03 CP3.2 : contact de confiance ; indéchiffrable = null, jamais bloquant.
        let trustedContact = null;
        if (row.trusted_contact_encrypted && row.trusted_contact_iv) {
          try {
            trustedContact = await decryptData(
              row.trusted_contact_encrypted,
              row.trusted_contact_iv,
              masterKey,
              AAD_TRUSTED_CONTACT_V1(row.id)
            );
          } catch {
            trustedContact = null;
          }
        }
        decrypted.push({ ...row, ...payload, trustedContact });

        // K46 — backfill lazy : si email_hash absent, le calculer et mettre à jour la ligne.
        if (!row.email_hash && payload.email) {
          getEmailHash(payload.email).then((hash) => {
            if (!hash) {
              console.error('[SafeHeir] K46 backfill email_hash échoué pour bénéficiaire', row.id);
              return;
            }
            supabase
              .from('beneficiaries')
              .update({ email_hash: hash })
              .eq('id', row.id)
              .then(({ error: updErr }) => {
                if (updErr) {
                  console.error('[SafeHeir] K46 UPDATE email_hash échoué', row.id, updErr.message);
                }
              });
          });
        }
      } catch {
        failedCount++;
      }
    }

    return { success: true, data: decrypted, failedCount };
  } catch (e) {
    return { success: false, error: e.message, data: [], failedCount: 0 };
  }
};

// ─── Création ──────────────────────────────────────────────────────────────

/**
 * Crée un bénéficiaire chiffré sous MK.
 * Si data contient la clé access_categories (présence = intention explicite,
 * même tableau vide), valide les catégories et journalise l'acte de
 * désignation : designation_form_version = 'v1'.
 * designation_consent_at est posé par DEFAULT now() côté DB (K40) —
 * timestamp serveur immuable, valeur probatoire RGPD.
 * T-S4-ATTR-03 — Spec V2 §4
 */
export const create = async (data, masterKey) => {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé — veuillez vous reconnecter.' };
  if (!data.designator_consent) {
    return { success: false, error: 'Consentement RGPD requis pour désigner un bénéficiaire.' };
  }
  try {
    const itemId  = crypto.randomUUID();
    const payload = buildPayload(data);

    if (!payload.name || !payload.email) {
      return { success: false, error: 'Nom et email obligatoires' };
    }

    // K102 : validation locale AVANT tout appel reseau
    if ('access_categories' in data) {
      const validation = validateAccessCategories(data.access_categories);
      if (!validation.valid) {
        return { success: false, error: `Catégories d'attribution invalides : ${validation.invalidCategories.join(', ')}` };
      }
    }

    const { ciphertext, iv } = await encryptData(payload, masterKey, aad(itemId));

    const { data: { user: sessionUser } } = await supabase.auth.getUser();
    if (!sessionUser?.id) {
      return { success: false, error: 'Session invalide. Veuillez vous reconnecter.' };
    }

    // K46 — calcul email_hash (apres validation locale, K102)
    const emailHash = await getEmailHash(payload.email);
    if (emailHash === null) {
      return { success: false, error: 'Impossible de calculer le hash email. Vérifiez votre connexion ou contactez le support.' };
    }

    // T-S3-fondation — jeton de confirmation (32 octets aléatoires, encodé base64url).
    // Seul le SHA-256 hex est stocké en base — le jeton brut est retourné à l'appelant.
    let confirmationToken;
    let confirmationTokenHash;
    let confirmationTokenExpires;
    try {
      ({ token: confirmationToken, hash: confirmationTokenHash, expiresAt: confirmationTokenExpires } = await generateConfirmationToken());
    } catch {
      return { success: false, error: 'Impossible de générer le jeton de confirmation. Vérifiez votre navigateur.' };
    }

    const designatorConsentTextHash = await computeConsentTextHash();

    const insertRow = {
      id:                              itemId,
      user_id:                         sessionUser.id,
      encrypted_data:                  ciphertext,
      iv,
      priority:                        data.priority || 1,
      status:                          'pending',
      email_hash:                      emailHash,
      confirmation_token_hash:         confirmationTokenHash,
      confirmation_token_expires_at:   confirmationTokenExpires,
      designator_consent_at:           new Date().toISOString(),
      designator_consent_version:      DESIGNATOR_CONSENT_VERSION,
      designator_consent_text_hash:    designatorConsentTextHash,
    };

    if ('access_categories' in data) {
      insertRow.access_categories        = data.access_categories;
      insertRow.designation_form_version = CURRENT_DESIGNATION_FORM_VERSION;
    }

    const { data: row, error } = await supabase
      .from('beneficiaries')
      .insert(insertRow)
      .select()
      .single();

    if (error) return { success: false, error: error.message };

    return { success: true, data: { ...row, ...payload }, token: confirmationToken };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Modification ──────────────────────────────────────────────────────────

/**
 * Met à jour un bénéficiaire chiffré sous MK.
 * Si data contient la clé access_categories (présence = intention explicite,
 * même tableau vide), valide les catégories et met à jour designation_form_version = 'v1'.
 * designation_consent_at n'est PAS modifié à l'update (K40) — la valeur initiale
 * posée par DEFAULT now() à la création est immuable (timestamp serveur, valeur probatoire).
 * T-S4-ATTR-03 — Spec V2 §4
 */
export const update = async (id, data, masterKey) => {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé — veuillez vous reconnecter.' };
  try {
    const payload = buildPayload(data);

    if (!payload.name || !payload.email) {
      return { success: false, error: 'Nom et email obligatoires' };
    }

    // K102 : validation locale AVANT tout appel reseau
    if ('access_categories' in data) {
      const validation = validateAccessCategories(data.access_categories);
      if (!validation.valid) {
        return { success: false, error: `Catégories d'attribution invalides : ${validation.invalidCategories.join(', ')}` };
      }
    }

    const { ciphertext, iv } = await encryptData(payload, masterKey, aad(id));

    // K46 — recalcul email_hash (apres validation locale, K102)
    const emailHashUpd = await getEmailHash(payload.email);
    if (emailHashUpd === null) {
      return { success: false, error: 'Impossible de calculer le hash email. Vérifiez votre connexion ou contactez le support.' };
    }

    const updateRow = {
      encrypted_data: ciphertext,
      iv,
      priority:    data.priority || 1,
      updated_at:  new Date().toISOString(),
      email_hash:  emailHashUpd,
    };

    if ('access_categories' in data) {
      updateRow.access_categories        = data.access_categories;
      updateRow.designation_form_version = CURRENT_DESIGNATION_FORM_VERSION;
    }

    const { data: row, error } = await supabase
      .from('beneficiaries')
      .update(updateRow)
      .eq('id', id)
      .select()
      .single();

    if (error) return { success: false, error: error.message };

    return { success: true, data: { ...row, ...payload } };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Révocation (D1) ───────────────────────────────────────────────────────

/**
 * Révoque un bénéficiaire du titulaire courant. Remplace la suppression
 * physique (D1, invariant K110) : la ligne est conservée à titre de preuve.
 * Refusé si le bénéficiaire est déjà révoqué ou transmis (statuts terminaux,
 * également gardés en base par beneficiaries_guard_status_transitions).
 * @param {string} id
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export const revoke = async (id) => {
  try {
    const { data: { user: sessionUser } } = await supabase.auth.getUser();
    if (!sessionUser?.id) {
      return { success: false, error: 'Session invalide. Veuillez vous reconnecter.' };
    }

    const { data: current, error: readError } = await supabase
      .from('beneficiaries')
      .select('status')
      .eq('id', id)
      .eq('user_id', sessionUser.id)
      .single();

    if (readError || !current) return { success: false, error: 'Bénéficiaire introuvable.' };
    if (current.status === 'revoked') {
      return { success: false, error: 'Ce bénéficiaire est déjà révoqué.' };
    }
    if (current.status === 'transmitted') {
      return { success: false, error: 'Ce bénéficiaire a déjà reçu sa transmission : révocation impossible.' };
    }

    const { data: row, error } = await supabase
      .from('beneficiaries')
      .update({
        status:         'revoked',
        revoked_at:     new Date().toISOString(),
        revoked_reason: 'user_request',
      })
      .eq('id', id)
      .eq('user_id', sessionUser.id)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data: row };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Chemin de remise du code (PF-03 CP3.1, DEC-037) ───────────────────────

/**
 * Pose le chemin de remise du code de transmission d'un bénéficiaire du
 * titulaire courant. Refusé si la valeur est hors CHECK SQL (sans appel
 * réseau) ou si le bénéficiaire est révoqué, transmis ou expiré.
 * @param {string} id
 * @param {string} releasePath - Valeur de RELEASE_PATHS.
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const setReleasePath = async (id, releasePath) => {
  if (!isValidReleasePath(releasePath)) {
    return { success: false, error: 'Chemin de remise du code invalide.' };
  }
  try {
    const { data: { user: sessionUser } } = await supabase.auth.getUser();
    if (!sessionUser?.id) {
      return { success: false, error: 'Session invalide. Veuillez vous reconnecter.' };
    }

    const { data: current, error: readError } = await supabase
      .from('beneficiaries')
      .select('status')
      .eq('id', id)
      .eq('user_id', sessionUser.id)
      .single();

    if (readError || !current) return { success: false, error: 'Bénéficiaire introuvable.' };
    if (TERMINAL_STATUSES.includes(current.status)) {
      return { success: false, error: 'Chemin non modifiable : ce bénéficiaire est révoqué, transmis ou expiré.' };
    }

    const { error } = await supabase
      .from('beneficiaries')
      .update({ release_path: releasePath })
      .eq('id', id)
      .eq('user_id', sessionUser.id);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Contact de confiance (PF-03 CP3.2, D5, DEC-038) ───────────────────────

/**
 * Enregistre ou remplace le contact de confiance d'un bénéficiaire.
 * Payload { name, email } chiffré sous la MK (AAD safeheir-trusted-contact-v1),
 * seul le hash HMAC de l'email est stocké en clair (vérification send-email).
 * Validation locale avant tout appel réseau (K102). Refusé si le bénéficiaire
 * est révoqué, transmis ou expiré. Si l'email change, la date de notification
 * est remise à NULL (une nouvelle notification sera nécessaire).
 * @param {string} id
 * @param {{ name: string, email: string }} contact
 * @param {CryptoKey} masterKey
 * @returns {Promise<{ success: boolean, emailChanged?: boolean, error?: string }>}
 */
export const setTrustedContact = async (id, contact, masterKey) => {
  if (!masterKey) return { success: false, error: 'Coffre verrouillé — veuillez vous reconnecter.' };
  const name = contact?.name?.trim() || '';
  const email = contact?.email?.trim().toLowerCase() || '';
  if (!name) return { success: false, error: 'Le prénom du contact est obligatoire.' };
  if (!EMAIL_PATTERN.test(email)) return { success: false, error: 'Adresse email du contact invalide.' };

  try {
    const { data: { user: sessionUser } } = await supabase.auth.getUser();
    if (!sessionUser?.id) {
      return { success: false, error: 'Session invalide. Veuillez vous reconnecter.' };
    }

    const { data: current, error: readError } = await supabase
      .from('beneficiaries')
      .select('status, trusted_contact_email_hash')
      .eq('id', id)
      .eq('user_id', sessionUser.id)
      .single();

    if (readError || !current) return { success: false, error: 'Bénéficiaire introuvable.' };
    if (TERMINAL_STATUSES.includes(current.status)) {
      return { success: false, error: 'Contact non modifiable : ce bénéficiaire est révoqué, transmis ou expiré.' };
    }

    const emailHash = await getEmailHash(email);
    if (emailHash === null) {
      return { success: false, error: 'Impossible de calculer le hash email. Vérifiez votre connexion ou contactez le support.' };
    }

    const { ciphertext, iv } = await encryptData({ name, email }, masterKey, AAD_TRUSTED_CONTACT_V1(id));

    const emailChanged = current.trusted_contact_email_hash !== emailHash;
    const updateRow = {
      trusted_contact_encrypted:  ciphertext,
      trusted_contact_iv:         iv,
      trusted_contact_email_hash: emailHash,
    };
    if (emailChanged) updateRow.trusted_contact_notified_at = null;

    const { error } = await supabase
      .from('beneficiaries')
      .update(updateRow)
      .eq('id', id)
      .eq('user_id', sessionUser.id);

    if (error) return { success: false, error: error.message };
    return { success: true, emailChanged };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * Retire le contact de confiance : les quatre colonnes repassent à NULL
 * (UPDATE, jamais DELETE). Autorisé quel que soit le statut (minimisation
 * RGPD, C7) ; un retrait demandé par le contact lui-même passe par l'opérateur.
 * @param {string} id
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const clearTrustedContact = async (id) => {
  try {
    const { data: { user: sessionUser } } = await supabase.auth.getUser();
    if (!sessionUser?.id) {
      return { success: false, error: 'Session invalide. Veuillez vous reconnecter.' };
    }

    const { error } = await supabase
      .from('beneficiaries')
      .update({
        trusted_contact_encrypted:   null,
        trusted_contact_iv:          null,
        trusted_contact_email_hash:  null,
        trusted_contact_notified_at: null,
      })
      .eq('id', id)
      .eq('user_id', sessionUser.id);

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Renvoi d'invitation ───────────────────────────────────────────────────

/**
 * Régénère un jeton de confirmation pour un bénéficiaire existant (K52).
 * Usage : bénéficiaires status='pending' sans jeton valide (créés avant PR #36).
 * Ne touche PAS à status, responded_at, email_hash, ni aux données chiffrées.
 * Refusé si le bénéficiaire est révoqué ou transmis (statut terminal).
 * Retourne { success:true, token } ou { success:false, error }.
 */
export const resendInvitation = async (id) => {
  try {
    const { data: current, error: readError } = await supabase
      .from('beneficiaries')
      .select('status')
      .eq('id', id)
      .single();

    if (readError || !current) return { success: false, error: 'Bénéficiaire introuvable.' };
    if (TERMINAL_STATUSES.includes(current.status)) {
      return { success: false, error: 'Invitation impossible : ce bénéficiaire est révoqué ou a déjà reçu sa transmission.' };
    }

    const { token, hash, expiresAt } = await generateConfirmationToken();
    const { error } = await supabase
      .from('beneficiaries')
      .update({
        confirmation_token_hash:       hash,
        confirmation_token_expires_at: expiresAt,
      })
      .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true, token };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

// ─── Migration one-shot localStorage → Supabase ────────────────────────────
// Appelée au premier chargement de BeneficiariesPage post-T-S3-01.
// Lit safeheir_beneficiaries, déchiffre avec la clé v2 legacy, rechiffre
// sous MK, INSERT Supabase, purge localStorage SI zéro échec.

export const migrateFromLocalStorage = async (masterKey, userId, userEmail) => {
  if (!masterKey || !userId || !userEmail) return { success: false, error: 'Paramètres manquants (masterKey, userId ou userEmail).', migrated: 0, failed: 0 };

  try {
    const raw = localStorage.getItem('safeheir_beneficiaries');
    if (!raw) return { migrated: 0, failed: 0 };

    const all  = JSON.parse(raw);
    const mine = all.filter((b) => b.user_id === userId);
    if (mine.length === 0) return { migrated: 0, failed: 0 };

    const CryptoJS  = (await import('crypto-js')).default;
    const legacyKey = CryptoJS.HmacSHA256(`${userId}:${userEmail}`, 'safeheir-vault-v1').toString();

    const decryptField = (enc) => {
      if (!enc) return null;
      try {
        const bytes = CryptoJS.AES.decrypt(enc, legacyKey);
        return bytes.toString(CryptoJS.enc.Utf8) || null;
      } catch { return null; }
    };

    let migrated = 0;
    let failed   = 0;

    for (const b of mine) {
      try {
        const payload = {
          name:     b.name_enc     ? decryptField(b.name_enc)     : (b.name     || ''),
          email:    b.email_enc    ? decryptField(b.email_enc)    : (b.email    || ''),
          phone:    b.phone_enc    ? decryptField(b.phone_enc)    : (b.phone    || null),
          relation: b.relation_enc ? decryptField(b.relation_enc) : (b.relation || null),
          message:  b.message_enc  ? decryptField(b.message_enc)  : (b.message  || null),
        };

        if (!payload.name || !payload.email) { failed++; continue; }

        const itemId              = crypto.randomUUID();
        const { ciphertext, iv }  = await encryptData(payload, masterKey, aad(itemId));

        const { error } = await supabase.from('beneficiaries').insert({
          id:             itemId,
          user_id:        userId,
          encrypted_data: ciphertext,
          iv,
          priority: b.priority || 1,
          status:   'pending',
        });

        if (error) { failed++; continue; }
        migrated++;
      } catch { failed++; }
    }

    if (failed === 0 && migrated > 0) {
      const remaining = all.filter((b) => b.user_id !== userId);
      if (remaining.length === 0) {
        localStorage.removeItem('safeheir_beneficiaries');
      } else {
        localStorage.setItem('safeheir_beneficiaries', JSON.stringify(remaining));
      }
    }

    return { migrated, failed };
  } catch (e) {
    console.error('[SafeHeir] Migration bénéficiaires localStorage:', e);
    return { migrated: 0, failed: 0 };
  }
};
